import React, { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ProjectDataProvider } from "./projects/ProjectDataContext";

/* ───────── CONTEXT + GATE ───────── */
import AuthGate from "./auth/AuthGate";

/* ───────── COMPONENT LAYOUT ───────── */
import MainLayout from "./components/MainLayout";

/* ───────── SIDEBARS ───────── */
import DashboardSidebar from "./sidebars/DashboardSidebar";
import AccountSidebar from "./sidebars/AccountSidebar";
import ProjectsSidebar from "./sidebars/ProjectsSidebar";

/* ───────── TOP-LEVEL / MISC PAGES ───────── */
import Home from "./pages/home";
import Signup from "./pages/signup";
import VerifyEmail from "./pages/verify";
import Login from "./pages/login";

/* ───────── DASHBOARD SUB-PAGES ───────── */
import DashboardOverview from "./pages/dashboard/Overview";
import AccountProjectsPage from "./pages/account/projects";
import ProjectOverview from "./pages/projects/Overview";
import CalendarView from "./pages/projects/Calendar";
import GanttChartView from "./pages/projects/GanttChart";
import KanbanBoardView from "./pages/projects/KanbanBoard";

/* ───────── ACCOUNT SUB-PAGES ───────── */
import AccountHome from "./pages/account/AccountHome";
import AccountSubscription from "./pages/account/Subscription";
import AccountOrganizations from "./pages/account/Organizations";
import ChatLog from "./pages/account/ChatLog";

/* ───────── ORGANIZATION SETTINGS ───────── */
import OrganizationSettings from "./pages/organizations/[orgId]";
import AcceptInvite from "./pages/invite/[token]";
import ProjectDetailsPageWrapper from "./pages/account/projects/[projectId]";

const GA_MEASUREMENT_ID = "G-Z7HM688ZH7";

export default function App() {
  const location = useLocation();

  useEffect(() => {
    const script = document.createElement("script");
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.async = true;
    document.head.appendChild(script);

    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).gtag = function gtag(...args: any[]) {
      (window as any).dataLayer.push(args);
    };

    (window as any).gtag("js", new Date());
    (window as any).gtag("config", GA_MEASUREMENT_ID);
  }, []);

  useEffect(() => {
    (window as any).gtag?.("config", GA_MEASUREMENT_ID, {
      page_path: location.pathname,
    });
  }, [location]);

  return (
    <div className="bp5-light">
      <ProjectDataProvider>
        <Routes>
          {/* PUBLIC HOME */}
          <Route path="/" element={<Home />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/verify" element={<VerifyEmail />} />
          <Route path="/login" element={<Login />} />

          {/* DASHBOARD */}
          <Route
            path="/dashboard"
            element={
              <AuthGate>
                <MainLayout pageTitle="Dashboard" LeftBar={DashboardSidebar} />
              </AuthGate>
            }
          >
            <Route index element={<DashboardOverview />} />
          </Route>

          {/* PROJECTS */}
          <Route
            path="/projects"
            element={
              <AuthGate>
                <MainLayout pageTitle="Projects" LeftBar={ProjectsSidebar} />
              </AuthGate>
            }
          >
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<ProjectOverview />} />
            <Route path="calendar" element={<CalendarView />} />
            <Route path="gantt" element={<GanttChartView />} />
            <Route path="kanban" element={<KanbanBoardView />} />
          </Route>

          {/* ACCOUNT */}
          <Route
            path="/account"
            element={
              <AuthGate>
                <MainLayout pageTitle="Account" LeftBar={AccountSidebar} />
              </AuthGate>
            }
          >
            <Route index element={<AccountHome />} />
            <Route path="subscription" element={<AccountSubscription />} />
            <Route path="organizations" element={<AccountOrganizations />} />
            <Route path="projects" element={<AccountProjectsPage />} />
            <Route path="projects/:projectId" element={<ProjectDetailsPageWrapper />} />
            <Route path="chat-log" element={<ChatLog />} />
          </Route>

          {/* ORGANIZATION SETTINGS (dynamic subpage) */}
          <Route
            path="/organizations/:orgId"
            element={
              <AuthGate>
                <MainLayout pageTitle="Organization Settings" LeftBar={DashboardSidebar}>
                  <OrganizationSettings />
                </MainLayout>
              </AuthGate>
            }
          />


          <Route path="/invite/:token" element={<AcceptInvite />} />

          {/* FALLBACK */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </ProjectDataProvider>
    </div>
  );
}
